import React, { ComponentType, createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';

// Add a type for subscribers
type DialogStateSubscriber = (dialogState: DialogState) => void;

/**
 * Configuration for registering a dialog with the dialog manager.
 * @interface DialogConfig
 */
interface DialogConfig {
  /**
   * Function that returns a promise resolving to the dialog component.
   * Used for lazy loading components to improve performance.
   */
  component: () => Promise<{ default: ComponentType<any> }>;
  /**
   * Optional default props to be passed to the dialog component.
   * These can be overridden when opening the dialog.
   */
  props?: Record<string, any>;
}

/**
 * Internal state representation for managing dialogs.
 * @interface DialogState
 */
interface DialogState {
  [key: string]: {
    /** Whether the dialog is currently open */
    open: boolean;
    /** The loaded component (undefined while loading) */
    Component?: ComponentType<any>;
    /** Props to pass to the dialog component */
    props?: Record<string, any>;
    /** Whether the dialog component is currently loading */
    loading: boolean;
    /** Unique ID for this dialog instance to help React with reconciliation */
    instanceId: string;
  };
}

/**
 * Context interface providing dialog management functionality.
 * @interface DialogManagerContextType
 */
interface DialogManagerContextType { 
  /**
   * The current dialog registry
   * @returns The current dialog registry
   */
  dialogRegistry: Record<string, DialogConfig>;
  
  /**
   * Registers a dialog with the dialog manager.
   * @param type - Unique identifier for the dialog
   * @param config - Configuration for the dialog including component and optional props
   */
  registerDialog: (type: string, config: DialogConfig) => void;
  
  /**
   * Opens a registered dialog.
   * @param type - Identifier of the dialog to open
   * @param props - Optional props to pass to the dialog, overriding default props
   * @returns Promise that resolves when the dialog component is loaded
   */
  openDialog: (type: string, props?: Record<string, any>) => Promise<void>;
  
  /**
   * Closes an open dialog.
   * @param type - Identifier of the dialog to close
   */
  closeDialog: (type: string) => void;
  
  /**
   * Checks if a dialog is currently open.
   * @param type - Identifier of the dialog to check
   * @returns Boolean indicating whether the dialog is open
   */
  isDialogOpen: (type: string) => boolean;
  
  /**
   * Checks if a dialog has been registered.
   * @param type - Identifier of the dialog to check
   * @returns Boolean indicating whether the dialog is registered
   */
  isDialogRegistered: (type: string) => boolean;
  
  /**
   * Get the current dialog state for all dialogs
   * @returns The current dialog state
   */
  getDialogState: () => DialogState;
}

// Create context with undefined default value
const DialogManagerContext = createContext<DialogManagerContextType | undefined>(undefined);

// Generate a unique ID for dialog instances
const generateId = () => `dialog_${Math.random().toString(36).substr(2, 9)}`;

/**
 * Provider component that manages dialog state and rendering.
 * Wrap your application with this provider to use the dialog management system.
 */
export const DialogManagerProvider: React.FC<{children: ReactNode}> = ({ children }) => {
  const [dialogRegistry, setDialogRegistry] = useState<Record<string, DialogConfig>>({});
  const [dialogState, setDialogState] = useState<DialogState>({});
  const isMounted = useRef(true);
  const subscribers = useRef<DialogStateSubscriber[]>([]);
  
  // Use useCallback to ensure stable function references
  const registerDialog = useCallback((type: string, config: DialogConfig) => {
    if (!isMounted.current) return;
    
    setDialogRegistry(prev => ({
      ...prev,
      [type]: config
    }));
  }, []);

  const isDialogRegistered = useCallback((type: string) => {
    return !!dialogRegistry[type];
  }, [dialogRegistry]);

  const openDialog = useCallback(async (type: string, props?: Record<string, any>) => {
    if (!dialogRegistry[type]) {
      console.error(`Dialog ${type} not registered`);
      return;
    }

    if (!isMounted.current) return;

    // Generate a unique ID for this dialog instance
    const instanceId = generateId();

    // First update state to show loading
    setDialogState(prev => {
      const updatedState = {
        ...prev,
        [type]: {
          ...(prev[type] || {}),
          open: true,
          loading: true,
          instanceId,
          props: {
            ...(dialogRegistry[type].props || {}),
            ...(props || {})
          }
        }
      };
      
      // Notify subscribers of state change
      notifySubscribers(updatedState);
      
      return updatedState;
    });

    try {
      // Load the component dynamically
      const { default: Component } = await dialogRegistry[type].component();
      
      // Check if component is still mounted before updating state
      if (isMounted.current) {
        setDialogState(prev => {
          // Make sure we're still working with the same instance
          if (prev[type]?.instanceId !== instanceId) return prev;
          
          const updatedState = {
            ...prev,
            [type]: {
              ...prev[type],
              Component,
              loading: false
            }
          };
          
          // Notify subscribers of state change
          notifySubscribers(updatedState);
          
          return updatedState;
        });
      }
    } catch (error) {
      console.error(`Failed to load dialog component for ${type}`, error);
      
      // Reset dialog state on error (if still mounted)
      if (isMounted.current) {
        setDialogState(prev => {
          // Make sure we're still working with the same instance
          if (prev[type]?.instanceId !== instanceId) return prev;
          
          const updatedState = {
            ...prev,
            [type]: {
              ...prev[type],
              open: false,
              loading: false
            }
          };
          
          // Notify subscribers of state change
          notifySubscribers(updatedState);
          
          return updatedState;
        });
      }
    }
  }, [dialogRegistry]);

  const closeDialog = useCallback((type: string) => {
    if (!isMounted.current) return;
    
    setDialogState(prev => {
      // If the dialog doesn't exist in state, just return current state
      if (!prev[type]) return prev;
      
      const updatedState = {
        ...prev,
        [type]: {
          ...prev[type],
          open: false
        }
      };
      
      // Notify subscribers of state change
      notifySubscribers(updatedState);
      
      return updatedState;
    });
  }, []);

  const isDialogOpen = useCallback((type: string) => {
    return !!dialogState[type]?.open;
  }, [dialogState]);
  
  const getDialogState = useCallback(() => {
    return dialogState;
  }, [dialogState]);
  
  // Function to notify all subscribers
  const notifySubscribers = (state: DialogState) => {
    subscribers.current.forEach(subscriber => subscriber(state));
  };
  
  // Subscribe to dialog state changes
  const subscribeToDialogState = useCallback((subscriber: DialogStateSubscriber) => {
    subscribers.current.push(subscriber);
    
    // Immediately notify the new subscriber with current state
    subscriber(dialogState);
    
    // Return unsubscribe function
    return () => {
      subscribers.current = subscribers.current.filter(sub => sub !== subscriber);
    };
  }, [dialogState]);
  
  // Expose the subscription function globally for DialogRegistryService to use
  useEffect(() => {
    (window as any).__DIALOG_MANAGER_SUBSCRIBE_TO_STATE__ = subscribeToDialogState;
    
    return () => {
      delete (window as any).__DIALOG_MANAGER_SUBSCRIBE_TO_STATE__;
    };
  }, [subscribeToDialogState]);

  // Clean up when the provider unmounts
  React.useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  return (
    <DialogManagerContext.Provider value={{
      dialogRegistry,
      registerDialog, 
      openDialog, 
      closeDialog,
      isDialogOpen,
      isDialogRegistered,
      getDialogState
    }}>
      {children}
      {/* Dialog rendering moved to DialogRegistryService */}
    </DialogManagerContext.Provider>
  );
};

/**
 * Hook to access the dialog manager functionality.
 * Must be used within a DialogManagerProvider.
 * 
 * @returns Dialog manager methods for registering, opening and closing dialogs
 * @throws Error if used outside of DialogManagerProvider
 */
export const useDialogManager = () => {
  const context = useContext(DialogManagerContext);
  if (!context) {
    throw new Error('useDialogManager must be used within a DialogManagerProvider');
  }
  return context;
};

export default DialogManagerProvider; 